import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, X, Loader2, BookOpen, CheckCircle, Clock, ChevronRight, Sparkles, BarChart2, Users, ShieldCheck, UtensilsCrossed, GlassWater, GraduationCap, RotateCcw, Pencil, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { useTraining, modeOf, type QuizMode, type TrainingCourse, type TrainingModule, type QuizQuestion } from '@/src/hooks/useTraining';
import { useTeamAccess } from '@/src/hooks/useTeamAccess';
import { useOrgId } from '@/src/hooks/useOrgId';
import type { AppRole } from '@/src/lib/types';
import { supabase } from '@/src/lib/supabase';
import { ManagerTraining, StaffTraining } from './training-overview';
import { useAppColors } from '@/src/lib/theme';
import { AI_FN_SUFFIX } from '@/src/lib/aiFunctions';

interface TrainingPageProps {
  venueId: string;
  userId: string;
  userRole: AppRole;
  /** Open this course straight away (from a task's "Take the quiz" button). */
  openCourseId?: string | null;
  onOpenedCourse?: () => void;
}

async function generateQuizWithAI(content: string, count: number, difficulty: 'easy' | 'medium' | 'hard' = 'medium', docName?: string | null): Promise<QuizQuestion[]> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-quiz${AI_FN_SUFFIX}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ content, count, difficulty, title: docName || undefined }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data.questions;
}

function getCategoryStyle(category: string | null): {
  icon: React.ReactNode;
  accent: string;
  iconBg: string;
  pillBg: string;
  pillBorder: string;
  pillText: string;
  actionBg: string;
  actionBorder: string;
  actionText: string;
} {
  const cat = (category ?? '').toLowerCase();
  if (cat.includes('bar') || cat.includes('drink') || cat.includes('bev')) return {
    icon: <GlassWater size={20} />,
    accent: '#4A9A4A',
    iconBg: 'rgba(100,180,100,0.12)',
    pillBg: 'rgba(100,180,100,0.1)', pillBorder: 'rgba(100,180,100,0.2)', pillText: '#8AE08A',
    actionBg: 'rgba(100,180,100,0.08)', actionBorder: 'rgba(100,180,100,0.2)', actionText: '#8AE08A',
  };
  if (cat.includes('food') || cat.includes('kitchen') || cat.includes('menu')) return {
    icon: <UtensilsCrossed size={20} />,
    accent: '#E8A84C',
    iconBg: 'rgba(240,160,60,0.12)',
    pillBg: 'rgba(240,160,60,0.1)', pillBorder: 'rgba(240,160,60,0.2)', pillText: '#F0B860',
    actionBg: 'rgba(240,160,60,0.08)', actionBorder: 'rgba(240,160,60,0.2)', actionText: '#F0B860',
  };
  if (cat.includes('safe') || cat.includes('hygiene') || cat.includes('compliance')) return {
    icon: <ShieldCheck size={20} />,
    accent: '#5A50A0',
    iconBg: 'rgba(127,119,221,0.12)',
    pillBg: 'rgba(127,119,221,0.1)', pillBorder: 'rgba(127,119,221,0.2)', pillText: '#B0A8F8',
    actionBg: 'rgba(127,119,221,0.1)', actionBorder: 'rgba(127,119,221,0.25)', actionText: '#B0A8F8',
  };
  if (cat.includes('service') || cat.includes('floor') || cat.includes('foh')) return {
    icon: <Users size={20} />,
    accent: '#4A8AAA',
    iconBg: 'rgba(55,138,221,0.12)',
    pillBg: 'rgba(55,138,221,0.1)', pillBorder: 'rgba(55,138,221,0.2)', pillText: '#70B8F8',
    actionBg: 'rgba(55,138,221,0.08)', actionBorder: 'rgba(55,138,221,0.2)', actionText: '#70B8F8',
  };
  return {
    icon: <BookOpen size={20} />,
    accent: '#5A50A0',
    iconBg: 'rgba(127,119,221,0.12)',
    pillBg: 'rgba(127,119,221,0.1)', pillBorder: 'rgba(127,119,221,0.2)', pillText: '#B0A8F8',
    actionBg: 'rgba(127,119,221,0.1)', actionBorder: 'rgba(127,119,221,0.25)', actionText: '#B0A8F8',
  };
}

// ── Quiz component ──────────────────────────────────────────────
function QuizView({ questions, onComplete, reviewOnly, moduleId, mode = 'test' }: { questions: QuizQuestion[]; onComplete: (score: number, answers: number[]) => void; reviewOnly?: boolean; moduleId?: string; mode?: QuizMode }) {
  const c = useAppColors();
  const learning = mode === 'learning';
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  // Learning mode: the order questions come in (missed ones are added again at the end, once),
  // the first answer given to each question (that's what's scored), and whether the current answer has been checked.
  const [queue, setQueue] = useState<number[]>(() => questions.map((_, i) => i));
  const [first, setFirst] = useState<Array<number | undefined>>([]);
  const [checked, setChecked] = useState(false);
  // AI notes for wrong answers, from the venue's documents (null = still loading, {} = none/failed).
  const [coach, setCoach] = useState<Record<number, string> | null>(null);

  async function loadCoach(final: number[]) {
    if (learning) { setCoach({}); return; } // already explained question by question
    const anyWrong = final.some((a, i) => a !== questions[i].correct);
    if (!anyWrong || !moduleId) { setCoach({}); return; }
    setCoach(null);
    try {
      const { data } = await supabase.functions.invoke('quiz-feedback', { body: { module_id: moduleId, answers: final } });
      const out: Record<number, string> = {};
      for (const f of ((data as { feedback?: Array<{ index: number; text: string }> } | null)?.feedback ?? [])) out[f.index] = f.text;
      setCoach(out);
    } catch { setCoach({}); }
  }

  if (reviewOnly) {
    return (
      <div className="space-y-4">
        <p className="text-xs font-bold text-cream-muted uppercase tracking-widest">Correct answers</p>
        {questions.map((q, i) => (
          <div key={i} className="rounded-xl border p-4 space-y-2" style={{ background: 'rgba(100,180,100,0.05)', borderColor: 'rgba(100,180,100,0.15)' }}>
            <p className="text-sm font-medium text-cream">{i + 1}. {q.question}</p>
            <div className="space-y-1">
              {q.options.map((opt, idx) => (
                <div key={idx} className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 ${idx === q.correct ? 'text-leaf-text font-semibold' : 'text-cream-muted opacity-50'}`}>
                  <span>{String.fromCharCode(65 + idx)}.</span>
                  <span>{opt}</span>
                  {idx === q.correct && <span className="ml-auto">✓</span>}
                </div>
              ))}
            </div>
            {q.explanation && <p className="text-xs leading-relaxed pt-1" style={{ color: c.textDim }}>{q.explanation}</p>}
          </div>
        ))}
      </div>
    );
  }

  function finish(final: number[]) {
    const correct = final.filter((a, i) => a === questions[i].correct).length;
    const score = Math.round((correct / questions.length) * 100);
    setAnswers(final);
    setShowResult(true);
    onComplete(score, final);
    void loadCoach(final);
  }

  function handleLearningNext() {
    if (selected === null) return;
    if (!checked) {
      // First press: lock the answer in and show why.
      const qi = queue[current];
      if (first[qi] === undefined) {
        setFirst((f) => { const n = [...f]; n[qi] = selected; return n; });
        if (selected !== questions[qi].correct) setQueue((q) => [...q, qi]);
      }
      setChecked(true);
      return;
    }
    // Second press: move on.
    const qLen = queue.length;
    setChecked(false);
    setSelected(null);
    if (current < qLen - 1) { setCurrent((x) => x + 1); return; }
    finish(questions.map((q, i) => first[i] ?? q.correct));
  }

  function handleNext() {
    if (learning) { handleLearningNext(); return; }
    if (selected === null) return;
    const newAnswers = [...answers, selected];
    if (current < questions.length - 1) {
      setAnswers(newAnswers);
      setSelected(null);
      setCurrent(c => c + 1);
    } else {
      const correct = newAnswers.filter((a, i) => a === questions[i].correct).length;
      const score = Math.round((correct / questions.length) * 100);
      setAnswers(newAnswers);
      setShowResult(true);
      onComplete(score, newAnswers);
      void loadCoach(newAnswers);
    }
  }

  if (showResult) {
    const correct = answers.filter((a, i) => a === questions[i].correct).length;
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= 70;
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
        <div className="flex flex-col items-center gap-3 py-6">
          <div className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-semibold"
            style={{ background: passed ? 'rgba(100,180,100,0.15)' : 'rgba(220,80,80,0.15)', color: passed ? '#8AE08A' : '#F08080' }}>
            {score}%
          </div>
          <h3 className="text-xl font-semibold text-cream">{passed ? 'Passed!' : 'Not quite'}</h3>
          <p className="text-sm text-cream-muted">{correct} of {questions.length} correct</p>
          {!passed && <p className="text-xs text-cream-muted">You need 70% to pass. Try again!</p>}
        </div>
        <p className="text-xs font-bold text-cream-muted uppercase tracking-widest">Review answers</p>
        {questions.map((q, i) => {
          const userAnswer = answers[i];
          const isCorrect = userAnswer === q.correct;
          return (
            <div key={i} className="rounded-xl border p-4 space-y-2"
              style={{ background: isCorrect ? 'rgba(100,180,100,0.05)' : 'rgba(220,80,80,0.05)', borderColor: isCorrect ? 'rgba(100,180,100,0.2)' : 'rgba(220,80,80,0.2)' }}>
              <p className="text-sm font-medium text-cream">{i + 1}. {q.question}</p>
              <div className="space-y-1">
                {q.options.map((opt, idx) => (
                  <div key={idx} className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-2 ${
                    idx === q.correct ? 'text-leaf-text font-semibold' :
                    idx === userAnswer && !isCorrect ? 'text-red-400 line-through' : 'text-cream-muted'
                  }`}>
                    {idx === q.correct ? <span>✓</span> : idx === userAnswer && !isCorrect ? <span>✗</span> : <span className="w-3 inline-block" />}
                    {opt}
                  </div>
                ))}
              </div>
              {!isCorrect ? (
                coach === null ? (
                  <p className="text-xs flex items-center gap-1.5 pt-1" style={{ color: c.textFaint }}><Loader2 size={12} className="animate-spin" /> OpsAlo is checking this one against your documents…</p>
                ) : coach[i] ? (
                  <div className="flex gap-2 rounded-lg px-3 py-2 mt-1" style={{ background: c.iconBg }}>
                    <Sparkles size={13} className="mt-0.5 shrink-0" style={{ color: c.accentSoft }} />
                    <p className="text-xs leading-relaxed" style={{ color: c.text }}>{coach[i]}</p>
                  </div>
                ) : q.explanation ? <p className="text-xs leading-relaxed pt-1" style={{ color: c.textDim }}>{q.explanation}</p> : null
              ) : q.explanation ? <p className="text-xs leading-relaxed pt-1" style={{ color: c.textDim }}>{q.explanation}</p> : null}
            </div>
          );
        })}
      </motion.div>
    );
  }

  const qi = learning ? queue[current] : current;
  const q = questions[qi];
  const total = learning ? queue.length : questions.length;
  const inRetry = learning && current >= questions.length;
  const isLast = current >= total - 1;
  const right = checked && selected === q.correct;
  const buttonLabel = learning
    ? (!checked ? 'Check answer' : isLast ? 'See results' : 'Next →')
    : (current < questions.length - 1 ? 'Next question →' : 'Submit quiz');
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-cream-muted">
          {inRetry ? 'One more go at the ones you missed' : `Question ${Math.min(current + 1, questions.length)} of ${questions.length}`}
          {learning && <span className="ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ background: c.iconBg, color: c.textDim }}>Learning</span>}
        </span>
        <div className="flex gap-1 flex-wrap justify-end">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className="w-6 h-1 rounded-full transition-all" style={{ background: i < current ? '#8AE08A' : i === current ? '#FAC775' : c.border }} />
          ))}
        </div>
      </div>
      <h3 className="text-base font-medium text-cream leading-relaxed">{q.question}</h3>
      <div className="space-y-2">
        {q.options.map((opt, idx) => {
          // After checking (learning): the right answer goes green, a wrong pick goes red.
          const isRight = checked && idx === q.correct;
          const isWrongPick = checked && idx === selected && idx !== q.correct;
          const on = selected === idx;
          return (
            <button key={idx} onClick={() => { if (!checked) setSelected(idx); }} disabled={checked}
              className="w-full text-left px-4 py-3 rounded-xl border text-sm transition-all flex items-center gap-2"
              style={{
                background: isRight ? 'rgba(100,180,100,0.15)' : isWrongPick ? 'rgba(220,80,80,0.12)' : on ? 'rgba(100,180,100,0.15)' : c.bgElevated,
                borderColor: isRight ? 'rgba(100,180,100,0.5)' : isWrongPick ? 'rgba(220,80,80,0.45)' : on ? 'rgba(100,180,100,0.4)' : c.border,
                color: on || isRight ? c.text : c.textDim,
              }}>
              <span className="font-medium" style={{ color: isWrongPick ? '#F08080' : on || isRight ? '#8AE08A' : c.textFaint }}>{String.fromCharCode(65 + idx)}.</span>
              <span className="flex-1">{opt}</span>
              {isRight && <CheckCircle size={15} style={{ color: '#8AE08A' }} />}
              {isWrongPick && <X size={15} style={{ color: '#F08080' }} />}
            </button>
          );
        })}
      </div>
      {checked && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl px-4 py-3 space-y-1"
          style={{ background: right ? 'rgba(100,180,100,0.08)' : 'rgba(220,80,80,0.07)', border: `1px solid ${right ? 'rgba(100,180,100,0.25)' : 'rgba(220,80,80,0.25)'}` }}>
          <p className="text-sm font-semibold" style={{ color: right ? '#8AE08A' : '#F08080' }}>
            {right ? 'Correct' : `Not quite. It's ${String.fromCharCode(65 + q.correct)}: ${q.options[q.correct]}`}
          </p>
          {q.explanation && <p className="text-[13px] leading-relaxed" style={{ color: c.text }}>{q.explanation}</p>}
          {!right && !inRetry && <p className="text-[12px]" style={{ color: c.textFaint }}>This one comes back at the end.</p>}
        </motion.div>
      )}
      <button onClick={handleNext} disabled={selected === null}
        className="w-full py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        style={{ background: selected !== null ? '#4A9A4A' : c.iconBg }}>
        {buttonLabel}
      </button>
    </div>
  );
}

// ── Course detail view ──────────────────────────────────────────
function CourseView({ course, venueId, userId, onBack, onComplete, reviewMode: reviewProp, isManager }: {
  course: TrainingCourse; venueId: string; userId: string;
  onBack: () => void; onComplete: (score: number) => void;
  reviewMode?: boolean; isManager?: boolean;
}) {
  const c = useAppColors();
  const { getModules, submitQuiz, startTest } = useTraining(venueId, userId);
  const mode = modeOf(course);
  // Learning quizzes can be practised again from the review screen.
  const [practise, setPractise] = useState(false);
  const reviewMode = reviewProp && !practise;
  const [confirmTest, setConfirmTest] = useState(false);
  const [startErr, setStartErr] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [finalScore, setFinalScore] = useState<number | null>(null);
  const style = getCategoryStyle(course.category);

  useEffect(() => {
    getModules(course.id).then(m => { setModules(m); setLoading(false); });
  }, [course.id, getModules]);

  const { assignments } = useTraining(venueId, userId);
  const myA = assignments.find((a) => a.course_id === course.id);
  // Staff get one go at a test: once it's been opened or finished, only a manager can reopen it.
  const testLocked = !isManager && mode === 'test' && !showQuiz && !submitted && !!(myA?.completed_at || myA?.started_at);
  useEffect(() => {
    if (reviewMode) {
      const a = assignments.find(a => a.course_id === course.id);
      if (a?.score != null) { setFinalScore(a.score); setSubmitted(true); }
    }
  }, [reviewMode, assignments, course.id]);

  const currentModule = modules[0];

  async function handleQuizComplete(score: number, answers: number[]) {
    const result = await submitQuiz(course.id, score, answers, mode);
    if (result.error) console.warn('Quiz submit:', result.error);
    setSubmitted(true);
    setFinalScore(score);
    onComplete(score);
  }

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={20} className="animate-spin text-cream-muted" /></div>;
  if (!currentModule) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3 text-cream-muted/50">
      <BookOpen size={32} />
      <p className="text-sm">No content yet.</p>
      <button onClick={onBack} className="text-xs text-leaf-text mt-2">← Back</button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-cream-muted hover:text-cream text-sm transition-colors">← Back</button>
        <div className="w-px h-4" style={{ background: c.border }} />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: style.iconBg, color: style.actionText }}>
            {style.icon}
          </div>
          <h2 className="text-lg font-semibold text-cream">{course.title}</h2>
          {course.category && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full border"
              style={{ background: style.pillBg, borderColor: style.pillBorder, color: style.pillText }}>
              {course.category}
            </span>
          )}
        </div>
      </div>

      {!showQuiz || reviewMode ? (
        <div className="space-y-4">
          {submitted && finalScore !== null ? (
            <div className="flex items-center gap-4 p-5 rounded-xl border"
              style={{ background: finalScore >= 70 ? 'rgba(100,180,100,0.06)' : 'rgba(220,80,80,0.06)', borderColor: finalScore >= 70 ? 'rgba(100,180,100,0.2)' : 'rgba(220,80,80,0.2)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-semibold flex-shrink-0"
                style={{ background: finalScore >= 70 ? 'rgba(100,180,100,0.15)' : 'rgba(220,80,80,0.15)', color: finalScore >= 70 ? '#8AE08A' : '#F08080' }}>
                {finalScore}%
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: finalScore >= 70 ? '#8AE08A' : '#F08080' }}>
                  {finalScore >= 70 ? '✓ Completed & Passed' : mode === 'test' ? "✗ Test finished, didn't pass" : '✗ Completed — needs retry'}
                </p>
                <p className="text-xs text-cream-muted mt-0.5">{mode === 'learning' ? 'Your best score is kept' : 'Your score has been recorded'}</p>
              </div>
              {mode === 'learning' && currentModule.quiz && currentModule.quiz.length > 0 && (
                <button onClick={() => { setPractise(true); setSubmitted(false); setFinalScore(null); setShowQuiz(true); }}
                  className="ml-auto h-9 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}>
                  <RotateCcw size={12} /> Practise again
                </button>
              )}
            </div>
          ) : testLocked ? (
            <div className="p-5 rounded-xl border space-y-1" style={{ background: c.bgElevated, borderColor: c.border }}>
              <p className="text-sm font-semibold" style={{ color: c.text }}>{myA?.completed_at ? `Test done${myA.score != null ? ` · ${myA.score}%` : ''}` : 'Test already started'}</p>
              <p className="text-xs" style={{ color: c.textDim }}>Tests can only be taken once. Ask your manager if you need to sit it again.</p>
            </div>
          ) : confirmTest ? (
            <div className="p-5 rounded-xl border space-y-3" style={{ background: c.bgElevated, borderColor: c.border }}>
              <p className="text-sm font-semibold" style={{ color: c.text }}>This is a test. You get one attempt.</p>
              <p className="text-[13px] leading-relaxed" style={{ color: c.textDim }}>
                {currentModule.quiz?.length ?? 0} questions, about {Math.max(2, Math.round((currentModule.quiz?.length ?? 0) / 2))} minutes. Once you start you can't restart, so make sure you have time and don't close the app. You'll see your score and the answers at the end.
              </p>
              {startErr && <p className="text-xs" style={{ color: c.danger }}>{startErr}</p>}
              <div className="flex gap-2">
                <button onClick={() => { setConfirmTest(false); setStartErr(null); }} className="h-11 px-4 rounded-xl text-sm font-semibold" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}>Not now</button>
                <button disabled={starting} onClick={async () => {
                  setStarting(true); setStartErr(null);
                  const r = await startTest(course.id);
                  setStarting(false);
                  if (r.error) { setStartErr(r.error); return; }
                  setConfirmTest(false); setShowQuiz(true);
                }} className="flex-1 h-11 rounded-xl text-sm font-semibold text-white disabled:opacity-50" style={{ background: '#4A9A4A' }}>
                  {starting ? 'Starting…' : 'Start test'}
                </button>
              </div>
            </div>
          ) : currentModule.quiz && currentModule.quiz.length > 0 ? (
            <div className="space-y-2">
              <button onClick={() => (mode === 'test' && !isManager ? setConfirmTest(true) : setShowQuiz(true))}
                className="w-full py-3.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
                style={{ background: style.actionBg, border: `1px solid ${style.actionBorder}`, color: style.actionText }}>
                <Sparkles size={15} /> {mode === 'test' ? (isManager ? 'Preview test' : 'Take test') : 'Start learning quiz'} · {currentModule.quiz.length} questions
              </button>
              <p className="text-xs text-center" style={{ color: c.textFaint }}>
                {mode === 'test' ? 'Test: one attempt, answers shown at the end.' : 'Learning: see the answer and why after each question. Take it as often as you like.'}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="rounded-xl border p-5" style={{ background: c.bgElevated, borderColor: c.border }}>
          <QuizView questions={currentModule.quiz!} onComplete={handleQuizComplete} reviewOnly={reviewMode} moduleId={currentModule.id} mode={mode} />
        </div>
      )}
    </div>
  );
}

// ── Quiz type picker (learning / test) ──────────────────────────
function ModePicker({ value, onChange, disabled }: { value: QuizMode; onChange: (m: QuizMode) => void; disabled?: boolean }) {
  const c = useAppColors();
  const opts: Array<{ key: QuizMode; title: string; text: string }> = [
    { key: 'learning', title: 'Learning', text: 'Answer and why after each question. Unlimited tries.' },
    { key: 'test', title: 'Test', text: 'One attempt. Score and answers at the end.' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {opts.map((o) => {
        const on = value === o.key;
        return (
          <button key={o.key} type="button" disabled={disabled} onClick={() => onChange(o.key)}
            className="text-left rounded-xl px-3 py-2.5 border transition-all disabled:opacity-50"
            style={on ? { background: 'rgba(100,180,100,0.12)', borderColor: 'rgba(100,180,100,0.35)' } : { background: 'transparent', borderColor: c.border }}>
            <p className="text-sm font-semibold" style={{ color: on ? '#8AE08A' : c.text }}>{o.title}</p>
            <p className="text-[11px] leading-snug mt-0.5" style={{ color: c.textDim }}>{o.text}</p>
          </button>
        );
      })}
    </div>
  );
}

// ── Create course form ──────────────────────────────────────────
// ── Quiz editor (managers) ───────────────────────────────────────
function QuizEditor({ course, venueId, userId, onBack }: { course: TrainingCourse; venueId: string; userId: string; onBack: () => void }) {
  const c = useAppColors();
  const { getModules, updateModuleQuiz, setCourseMode } = useTraining(venueId, userId);
  const [mode, setMode] = useState<QuizMode>(modeOf(course));
  const [modeMsg, setModeMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const changeMode = async (m: QuizMode) => {
    if (m === mode) return;
    const prev = mode;
    setMode(m); setModeMsg(null);
    const { error } = await setCourseMode(course.id, m);
    if (error) { setMode(prev); setModeMsg({ ok: false, text: error }); return; }
    course.quiz_mode = m;
    setModeMsg({ ok: true, text: m === 'test' ? 'Now a test: staff get one attempt.' : 'Now a learning quiz: staff can take it as often as they like.' });
  };
  const [moduleId, setModuleId] = useState<string | null>(null);
  const [qs, setQs] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    getModules(course.id).then((m) => {
      const first = m[0];
      setModuleId(first?.id ?? null);
      setQs((first?.quiz ?? []).map((q) => ({ ...q, options: [...q.options] })));
      setLoading(false);
    });
  }, [course.id, getModules]);

  const edit = (i: number, patch: Partial<QuizQuestion>) => { setQs((p) => p.map((q, j) => (j === i ? { ...q, ...patch } : q))); setDirty(true); setMsg(null); };
  const move = (i: number, d: -1 | 1) => { setQs((p) => { const n = [...p]; const j = i + d; if (j < 0 || j >= n.length) return p; [n[i], n[j]] = [n[j], n[i]]; return n; }); setDirty(true); };
  const remove = (i: number) => { if (window.confirm('Delete this question?')) { setQs((p) => p.filter((_, j) => j !== i)); setDirty(true); } };
  const add = () => { setQs((p) => [...p, { question: '', options: ['', '', '', ''], correct: 0, explanation: '' }]); setDirty(true); };

  const problems = qs.flatMap((q, i) => {
    const out: string[] = [];
    if (!q.question.trim()) out.push(`Question ${i + 1} has no text`);
    if (q.options.filter((o) => o.trim()).length < 2) out.push(`Question ${i + 1} needs at least 2 options`);
    if (!q.options[q.correct]?.trim()) out.push(`Question ${i + 1}: the correct answer is empty`);
    return out;
  });

  async function save() {
    if (!moduleId || problems.length) return;
    setSaving(true);
    // Drop empty options, keeping the correct answer pointing at the same option.
    const clean = qs.map((q) => {
      const kept = q.options.map((o, idx) => ({ o: o.trim(), idx })).filter((x) => x.o);
      return { question: q.question.trim(), options: kept.map((x) => x.o), correct: Math.max(0, kept.findIndex((x) => x.idx === q.correct)), ...(q.explanation?.trim() ? { explanation: q.explanation.trim() } : {}) };
    });
    const { error } = await updateModuleQuiz(moduleId, clean);
    setSaving(false);
    if (error) { setMsg({ ok: false, text: error }); return; }
    setQs(clean); setDirty(false); setMsg({ ok: true, text: 'Saved. Staff get the updated questions next time they take it.' });
  }

  const field = { background: c.iconBg, color: c.text, border: `1px solid ${c.border}` };
  if (loading) return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>;
  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center gap-3">
        <button onClick={() => { if (!dirty || window.confirm('Leave without saving your changes?')) onBack(); }} className="text-sm" style={{ color: c.textDim }}>← Back</button>
        <div className="w-px h-4" style={{ background: c.border }} />
        <h2 className="text-lg font-semibold truncate" style={{ color: c.text }}>Edit questions · {course.title}</h2>
      </div>
      {!moduleId ? <p className="text-sm" style={{ color: c.textDim }}>This course has no quiz yet.</p> : (
        <>
          <div className="space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textFaint }}>Quiz type</p>
            <ModePicker value={mode} onChange={(m) => void changeMode(m)} />
            {modeMsg && <p className="text-[12px]" style={{ color: modeMsg.ok ? c.accentSoft : c.danger }}>{modeMsg.text}</p>}
          </div>
          <p className="text-[13px]" style={{ color: c.textDim }}>Fix wording, change the right answer, or write your own questions. Tap the circle next to the correct option. The explanation is shown to staff after they answer.</p>
          {qs.map((q, i) => (
            <div key={i} className="rounded-xl p-4 space-y-3" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textFaint }}>Question {i + 1}</span>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg disabled:opacity-30" style={{ color: c.textDim }} aria-label="Move up"><ArrowUp size={14} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === qs.length - 1} className="p-1.5 rounded-lg disabled:opacity-30" style={{ color: c.textDim }} aria-label="Move down"><ArrowDown size={14} /></button>
                  <button onClick={() => remove(i)} className="p-1.5 rounded-lg" style={{ color: c.danger }} aria-label="Delete question"><Trash2 size={14} /></button>
                </div>
              </div>
              <textarea value={q.question} onChange={(e) => edit(i, { question: e.target.value })} rows={2} placeholder="Question"
                className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-y" style={field} />
              <div className="space-y-2">
                {q.options.map((opt, k) => (
                  <div key={k} className="flex items-center gap-2">
                    <button onClick={() => edit(i, { correct: k })} aria-label={`Mark option ${String.fromCharCode(65 + k)} correct`}
                      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold"
                      style={q.correct === k ? { background: c.accent, color: 'white' } : { border: `1.5px solid ${c.borderStrong}`, color: c.textFaint }}>
                      {q.correct === k ? '✓' : String.fromCharCode(65 + k)}
                    </button>
                    <input value={opt} onChange={(e) => edit(i, { options: q.options.map((o, j) => (j === k ? e.target.value : o)) })} placeholder={`Option ${String.fromCharCode(65 + k)}`}
                      className="flex-1 min-w-0 h-10 px-3 rounded-xl text-sm outline-none" style={field} />
                    {q.options.length > 2 && (
                      <button onClick={() => edit(i, { options: q.options.filter((_, j) => j !== k), correct: q.correct === k ? 0 : q.correct > k ? q.correct - 1 : q.correct })}
                        className="p-1.5" style={{ color: c.textFaint }} aria-label="Remove option"><X size={14} /></button>
                    )}
                  </div>
                ))}
                {q.options.length < 6 && (
                  <button onClick={() => edit(i, { options: [...q.options, ''] })} className="text-xs font-semibold" style={{ color: c.textDim }}>+ Add option</button>
                )}
              </div>
              <textarea value={q.explanation ?? ''} onChange={(e) => edit(i, { explanation: e.target.value })} rows={2} placeholder="Explanation shown after answering (optional), e.g. which document it comes from"
                className="w-full px-3 py-2 rounded-xl text-[13px] outline-none resize-y" style={field} />
            </div>
          ))}
          <button onClick={add} className="w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ background: c.iconBg, color: c.text, border: `1px dashed ${c.borderStrong}` }}>
            <Plus size={15} /> Add a question
          </button>
          {problems.length > 0 && dirty && <p className="text-[13px]" style={{ color: c.gold }}>{problems[0]}{problems.length > 1 ? ` (+${problems.length - 1} more)` : ''}</p>}
          {msg && <p className="text-[13px]" style={{ color: msg.ok ? c.accentSoft : c.danger }}>{msg.text}</p>}
          <button onClick={() => void save()} disabled={!dirty || saving || problems.length > 0 || qs.length === 0}
            className="w-full h-12 rounded-xl text-sm font-semibold disabled:opacity-40" style={{ background: c.accent, color: 'white' }}>
            {saving ? 'Saving…' : `Save ${qs.length} question${qs.length === 1 ? '' : 's'}`}
          </button>
        </>
      )}
    </div>
  );
}

function CreateCourseForm({ venueId, userId, members, onDone, onSaved }: {
  venueId: string; userId: string;
  members: { id: string; name: string; app_role: string }[];
  onDone: () => void; onSaved: () => Promise<void[]>;
}) {
  const c = useAppColors();
  const { createCourse, saveModule, assignCourse } = useTraining(venueId, userId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [questionCount, setQuestionCount] = useState(5);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [quizMode, setQuizMode] = useState<QuizMode>('learning');
  const [openAt, setOpenAt] = useState('');
  const [closeAt, setCloseAt] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [assignTo, setAssignTo] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'details' | 'content'>('details');
  const [selectedDoc, setSelectedDoc] = useState('');
  const [extracting, setExtracting] = useState(false);

  async function handleFileUpload(file: File) {
    setExtracting(true);
    setError(null);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      setSelectedDoc(file.name);
      setError('PDF extraction not supported on Safari — paste content manually or use Chrome.');
      setExtracting(false);
      return;
    }
    try {
      const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
      GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      const pages: string[] = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const c = await page.getTextContent();
        // Keep line breaks so menu items, descriptions and allergen notes stay together.
        pages.push(c.items.map((item: any) => ('str' in item ? item.str + (item.hasEOL ? '\n' : ' ') : '')).join('').replace(/[ \t]+\n/g, '\n'));
      }
      setContent(pages.join('\n\n'));
      setSelectedDoc(file.name);
    } catch {
      setError('Failed to read PDF. Try Chrome for best results.');
    }
    setExtracting(false);
  }

  async function handleSave() {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const questions = await generateQuizWithAI(content, questionCount, difficulty, selectedDoc);
      const { error: courseErr, data: course } = await createCourse({ title, description, category, quiz_mode: quizMode });
      if (courseErr || !course) throw new Error(courseErr ?? 'Failed to create course');
      const { error: moduleErr } = await saveModule({
        course_id: course.id,
        title,
        content_text: selectedDoc ? `Study the ${selectedDoc} before taking this quiz.` : content,
        quiz: questions,
        pass_threshold: 70,
        position: 1,
      });
      if (moduleErr) throw new Error(moduleErr);
      for (const memberId of assignTo) {
        await assignCourse(course.id, memberId, dueAt || undefined);
      }
      await onSaved();
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    }
    setSaving(false);
  }

  function toggleAssign(id: string) {
    setAssignTo(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  const inputStyle = "w-full rounded-lg px-4 py-2.5 text-sm text-cream outline-none transition-colors placeholder:text-cream-muted/30"
  const inputInlineStyle = { background: c.bgElevated, border: `1px solid ${c.border}` };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-cream">Create course</h2>
        <button onClick={onDone} className="text-cream-muted hover:text-cream transition-colors"><X size={18} /></button>
      </div>

      <div className="flex gap-2">
        {(['details', 'content'] as const).map(s => (
          <button key={s} onClick={() => setStep(s)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize"
            style={step === s ? { background: 'rgba(100,180,100,0.12)', color: '#8AE08A', borderColor: 'rgba(100,180,100,0.25)' } : { background: 'transparent', color: c.textFaint, borderColor: c.border }}>
            {s}
          </button>
        ))}
      </div>

      {step === 'details' ? (
        <div className="space-y-3">
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Course title e.g. Food Safety Basics"
            className={inputStyle} style={inputInlineStyle} />
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description (optional)"
            className={inputStyle} style={inputInlineStyle} />
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="w-full rounded-lg px-4 py-2.5 text-sm outline-none transition-colors" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, colorScheme: c.isDark ? 'dark' : 'light', appearance: 'auto', color: c.text }}>
            <option value="">Select category</option>
            <option value="Food">Food</option>
            <option value="Bar">Bar</option>
            <option value="Safety">Safety</option>
            <option value="Service">Service</option>
            <option value="Operations">Operations</option>
          </select>
          <div className="space-y-2">
            <p className="text-xs font-medium" style={{ color: c.textDim }}>Quiz type:</p>
            <ModePicker value={quizMode} onChange={setQuizMode} />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium" style={{ color: c.textDim }}>Difficulty:</p>
            <div className="flex gap-2">
              {(['easy', 'medium', 'hard'] as const).map(d => (
                <button key={d} onClick={() => setDifficulty(d)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all capitalize"
                  style={difficulty === d
                    ? { background: 'rgba(100,180,100,0.12)', borderColor: 'rgba(100,180,100,0.25)', color: '#8AE08A' }
                    : { background: 'transparent', borderColor: c.border, color: c.textFaint }}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium" style={{ color: c.textDim }}>Schedule (optional):</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs w-16" style={{ color: c.textFaint }}>Opens</span>
                <input type="datetime-local" value={openAt} onChange={e => setOpenAt(e.target.value)}
                  className={inputStyle} style={{ ...inputInlineStyle, colorScheme: c.isDark ? 'dark' : 'light' }} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs w-16" style={{ color: c.textFaint }}>Closes</span>
                <input type="datetime-local" value={closeAt} onChange={e => setCloseAt(e.target.value)}
                  className={inputStyle} style={{ ...inputInlineStyle, colorScheme: c.isDark ? 'dark' : 'light' }} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs w-16" style={{ color: c.textFaint }}>Due</span>
                <input type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)}
                  className={inputStyle} style={{ ...inputInlineStyle, colorScheme: c.isDark ? 'dark' : 'light' }} />
              </div>
            </div>
          </div>
          {members.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: c.textDim }}>Assign to staff (optional):</p>
              <div className="flex flex-wrap gap-2">
                {members.map(m => (
                  <button key={m.id} onClick={() => toggleAssign(m.id)}
                    className="px-3 py-1.5 rounded-lg text-xs border transition-all"
                    style={assignTo.includes(m.id)
                      ? { background: 'rgba(100,180,100,0.12)', borderColor: 'rgba(100,180,100,0.25)', color: '#8AE08A' }
                      : { background: 'transparent', borderColor: c.border, color: c.textDim }}>
                    {m.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => setStep('content')} disabled={!title.trim()}
            className="w-full py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-40 transition-colors"
            style={{ background: '#4A9A4A' }}>
            Next → Add content
          </button>
        </div>
      ) : (
        <div className="space-y-3">

          <div onClick={() => document.getElementById('course-file-input')?.click()}
            className="w-full border-2 border-dashed rounded-xl p-4 flex items-center gap-3 cursor-pointer transition-all"
            style={{ borderColor: selectedDoc ? 'rgba(100,180,100,0.3)' : c.border }}>
            {extracting ? <Loader2 size={18} className="animate-spin text-cream-muted" />
              : <GraduationCap size={18} style={{ color: selectedDoc ? '#8AE08A' : c.textFaint }} />}
            <div className="flex-1 min-w-0">
              <p className="text-sm" style={{ color: selectedDoc ? '#8AE08A' : c.textFaint }}>
                {extracting ? 'Loading...' : selectedDoc ? selectedDoc : 'Upload a PDF'}
              </p>
            </div>
            <input id="course-file-input" type="file" accept=".pdf" hidden
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }} />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: c.border }} />
            <span className="text-xs" style={{ color: c.textFaint }}>or paste manually</span>
            <div className="flex-1 h-px" style={{ background: c.border }} />
          </div>

          <textarea value={selectedDoc && !error ? '' : content} onChange={e => { setContent(e.target.value); setSelectedDoc(''); }}
            placeholder="Paste training content here..." rows={4} disabled={!!selectedDoc && !error}
            className="w-full rounded-lg px-4 py-3 text-sm text-cream outline-none resize-none disabled:opacity-40"
            style={{ ...inputInlineStyle }} />

          <div className="flex items-center gap-3">
            <span className="text-xs" style={{ color: c.textDim }}>Questions:</span>
            <div className="flex gap-1">
              {[3, 5, 8, 10].map(n => (
                <button key={n} onClick={() => setQuestionCount(n)}
                  className="w-9 h-8 rounded-lg text-xs font-semibold border transition-all"
                  style={questionCount === n
                    ? { background: 'rgba(100,180,100,0.12)', borderColor: 'rgba(100,180,100,0.25)', color: '#8AE08A' }
                    : { background: 'transparent', borderColor: c.border, color: c.textFaint }}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <button onClick={handleSave} disabled={!title.trim() || !content.trim() || saving}
            className="w-full py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
            style={{ background: '#4A9A4A' }}>
            {saving ? <><Loader2 size={14} className="animate-spin" /> Generating & saving...</> : <><Sparkles size={14} /> Generate quiz & save</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Manager results dashboard ───────────────────────────────────
function ResultsDashboard({ courses, allAssignments, members, onReset }: {
  courses: TrainingCourse[];
  allAssignments: { id: string; user_id: string; course_id: string; score: number | null; completed_at: string | null; attempts: number; due_at: string | null }[];
  members: { id: string; name: string; app_role: string }[];
  onReset: (userId: string) => Promise<void>;
}) {
  const c = useAppColors();
  // Build category averages per member
  const categories = Array.from(new Set(courses.map(c => (c.category ?? 'General').toLowerCase().trim())))
    .map(cat => ({ key: cat, label: cat.charAt(0).toUpperCase() + cat.slice(1), style: getCategoryStyle(cat) }));

  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-cream-muted uppercase tracking-widest">Staff competency</p>
      {members.length === 0 ? (
        <p className="text-xs text-cream-muted">No staff members yet.</p>
      ) : (
        <div className="space-y-3">
          {members.map(member => {
            const memberAssignments = allAssignments.filter(a => a.user_id === member.id);
            const completed = memberAssignments.filter(a => a.completed_at).length;
            const total = memberAssignments.length;
            const overallAvg = completed > 0
              ? Math.round(memberAssignments.filter(a => a.score != null).reduce((sum, a) => sum + (a.score ?? 0), 0) / completed)
              : null;

            // Category averages
            const catScores = categories.map(cat => {
              const catCourses = courses.filter(c => (c.category ?? 'General').toLowerCase().trim() === cat.key);
              const catAssignments = memberAssignments.filter(a => catCourses.some(c => c.id === a.course_id) && a.completed_at && a.score != null);
              const avg = catAssignments.length > 0
                ? Math.round(catAssignments.reduce((sum, a) => sum + (a.score ?? 0), 0) / catAssignments.length)
                : null;
              const pending = memberAssignments.filter(a => catCourses.some(c => c.id === a.course_id) && !a.completed_at).length;
              return { ...cat, avg, pending, count: catAssignments.length };
            }).filter(c => c.avg !== null || c.pending > 0);

            return (
              <div key={member.id} className="rounded-xl border overflow-hidden" style={{ background: c.bgElevated, borderColor: c.border }}>
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(100,180,100,0.15)', color: '#8AE08A' }}>
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-cream">{member.name}</p>
                      <p className="text-[11px] text-cream-muted capitalize">{member.app_role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-cream-muted">{completed}/{total} done</p>
                      {overallAvg !== null && (
                        <p className="text-xs font-semibold" style={{ color: overallAvg >= 70 ? '#8AE08A' : '#F08080' }}>
                          Overall {overallAvg}%
                        </p>
                      )}
                    </div>
                    {total > 0 && (
                      <button onClick={() => { if (window.confirm('Reset results for ' + member.name + '?')) onReset(member.id); }}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] border transition-all"
                        style={{ background: 'rgba(220,80,80,0.08)', borderColor: 'rgba(220,80,80,0.2)', color: '#F08080' }}>
                        <RotateCcw size={11} /> Reset
                      </button>
                    )}
                  </div>
                </div>
                {catScores.length > 0 && (
                  <div className="border-t px-4 py-3 flex flex-wrap gap-2" style={{ borderColor: c.border }}>
                    {catScores.map(cat => (
                      <div key={cat.key} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border"
                        style={{ background: cat.style.pillBg, borderColor: cat.style.pillBorder }}>
                        <div style={{ transform: 'scale(0.65)', color: cat.style.pillText }}>{cat.style.icon}</div>
                        <span className="text-[11px] font-medium" style={{ color: cat.style.pillText }}>{cat.label}</span>
                        {cat.avg !== null ? (
                          <span className="text-[11px] font-bold ml-1" style={{ color: cat.avg >= 70 ? '#8AE08A' : '#F08080' }}>{cat.avg}%</span>
                        ) : (
                          <span className="text-[11px] ml-1" style={{ color: 'rgba(240,160,60,0.7)' }}>Pending</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main training page ──────────────────────────────────────────
export default function TrainingPage({ venueId, userId, userRole, openCourseId, onOpenedCourse }: TrainingPageProps) {
  const c = useAppColors();
  const isManager = userRole === 'owner' || userRole === 'manager';
  const { courses, assignments, allAssignments, loading, deleteCourse, reload, assignCourse } = useTraining(venueId, userId);
  const visibleCourses = isManager ? courses : courses.filter(course => assignments.some(a => a.course_id === course.id));
  const { orgId } = useOrgId(venueId);
  const { members, onOpsAlo } = useTeamAccess(orgId, venueId);
  // Only people who can actually take a quiz (signed up, not switched off), plus anyone with results already.
  const trainees = members.filter((m) => m.active && (!!m.auth_user_id || allAssignments.some((a) => a.user_id === m.id)));
  void onOpsAlo;
  const [view, setView] = useState<'list' | 'create' | 'course' | 'course-review' | 'results' | 'edit'>('list');
  const [selectedCourse, setSelectedCourse] = useState<TrainingCourse | null>(null);
  const [lastScore, setLastScore] = useState<number | null>(null);
  const [lastWasTest, setLastWasTest] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);

  function getAssignment(courseId: string) {
    return assignments.find(a => a.course_id === courseId);
  }

  // Opened from a task's "Take the quiz" button: jump straight into that course.
  useEffect(() => {
    if (!openCourseId || loading) return;
    const course = courses.find((x) => x.id === openCourseId);
    if (course) {
      const a = assignments.find((x) => x.course_id === course.id);
      const passed = !!a?.completed_at && (a.score ?? 0) >= 70;
      setSelectedCourse(course); setView(passed ? 'course-review' : 'course'); setLastScore(null);
    }
    onOpenedCourse?.();
  }, [openCourseId, loading, courses, assignments, onOpenedCourse]);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 size={20} className="animate-spin text-cream-muted" /></div>;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
      className="px-4 sm:px-8 py-6 max-w-5xl mx-auto w-full pb-24">
      <AnimatePresence mode="wait">
        {view === 'create' ? (
          <motion.div key="create" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CreateCourseForm venueId={venueId} userId={userId} members={onOpsAlo} onDone={() => setView('list')} onSaved={reload} />
          </motion.div>
        ) : view === 'edit' && selectedCourse && isManager ? (
          <motion.div key="edit" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <QuizEditor course={selectedCourse} venueId={venueId} userId={userId} onBack={() => { setView('list'); setSelectedCourse(null); }} />
          </motion.div>
        ) : (view === 'course' || view === 'course-review') && selectedCourse ? (
          <motion.div key="course" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <CourseView course={selectedCourse} venueId={venueId} userId={userId} reviewMode={view === 'course-review'} isManager={isManager}
              onBack={() => { setView('list'); setSelectedCourse(null); setLastScore(null); }}
              onComplete={async (score) => { setLastScore(score); setLastWasTest(modeOf(selectedCourse) === 'test'); await reload(); }} />
          </motion.div>
        ) : (
          <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {isManager ? (
              <ManagerTraining venueId={venueId} courses={courses} allAssignments={allAssignments} members={trainees}
                reload={reload} assignCourse={assignCourse} lastScore={lastScore}
                onCreate={() => setView('create')}
                onOpen={(course) => { setSelectedCourse(course); setView('course'); setLastScore(null); }}
                onEdit={(course) => { setSelectedCourse(course); setView('edit'); }}
                onDelete={(course) => { if (window.confirm(`Delete "${course.title}"? Staff results for it are removed too.`)) void deleteCourse(course.id); }} />
            ) : (
              <StaffTraining venueId={venueId} courses={visibleCourses} assignments={assignments} lastScore={lastScore} lastWasTest={lastWasTest}
                onStart={(course, review) => { setSelectedCourse(course); setView(review ? 'course-review' : 'course'); setLastScore(null); }} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}